import React, { useRef, useCallback } from 'react';

function downloadTableAsCsv(table: HTMLTableElement) {
  const rows = Array.from(table.querySelectorAll('tr'));
  const csv = rows
    .map(row =>
      Array.from(row.querySelectorAll('th, td'))
        .map(cell => {
          const text = (cell as HTMLElement).innerText.replace(/"/g, '""');
          return `"${text}"`;
        })
        .join(',')
    )
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'roadmap-data.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function copyTableAsText(table: HTMLTableElement) {
  const rows = Array.from(table.querySelectorAll('tr'));
  const text = rows
    .map(row =>
      Array.from(row.querySelectorAll('th, td'))
        .map(cell => (cell as HTMLElement).innerText.trim())
        .join('\t')
    )
    .join('\n');
  navigator.clipboard.writeText(text).catch(() => {});
}

function TableActions({
  tableRef,
  onAskMore,
  hasMore,
  onRetry
}: {
  tableRef: React.RefObject<HTMLTableElement | null>;
  onAskMore: () => void;
  hasMore: boolean;
  onRetry?: () => void;
}) {
  const handleDownload = useCallback(() => {
    if (tableRef.current) downloadTableAsCsv(tableRef.current);
  }, [tableRef]);

  const handleCopy = useCallback(() => {
    if (tableRef.current) copyTableAsText(tableRef.current);
  }, [tableRef]);

  return (
    <div className="rau-table-actions">
      <div className="rau-table-actions-left">
        <button
          className="rau-table-action"
          onClick={handleDownload}
          title="Download as CSV"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 12l-4-4h2.5V3h3v5H12L8 12zM3 14h10v-1H3v1z" />
          </svg>
          Download CSV
        </button>
        <button
          className="rau-table-action"
          onClick={handleCopy}
          title="Copy table"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M5 5h7v7H5V5zm0-1a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5z" />
            <path d="M3 2v8H2V2a1 1 0 0 1 1-1h8v1H3z" />
          </svg>
          Copy
        </button>
        {onRetry && (
          <button className="rau-table-action" onClick={onRetry} title="Retry">
            <svg
              width="13"
              height="13"
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M8 2.5a5.5 5.5 0 1 0 5.5 5.5h-1A4.5 4.5 0 1 1 8 3.5v-1zm0-2.5v4l2-2-2-2z" />
            </svg>
            Retry
          </button>
        )}
        {hasMore && (
          <button className="rau-table-action" onClick={onAskMore}>
            Show more
          </button>
        )}
      </div>
    </div>
  );
}

export function TableWrapper({
  children,
  onAskMore,
  hasMore,
  onRetry
}: {
  children: React.ReactNode;
  onAskMore: () => void;
  hasMore: boolean;
  onRetry?: () => void;
}) {
  const tableRef = useRef<HTMLTableElement | null>(null);

  return (
    <div className="rau-table-container">
      <div className="rau-table-scroll">
        <table ref={tableRef}>{children}</table>
      </div>
      <TableActions
        tableRef={tableRef}
        onAskMore={onAskMore}
        hasMore={hasMore}
        onRetry={onRetry}
      />
    </div>
  );
}
